import {
  BookOpen,
  Calendar,
  Coins,
  Infinity as InfinityIcon,
  RefreshCw,
  RotateCcw,
  Star,
  TimerOff,
  XCircle,
  Zap,
} from "lucide-react"
import type { PlanCategory, PlanName } from "@/lib/config/plans"

type PlanCardConfig = {
  displayName: string
  description: string
  features: string[]
  icon: React.ReactNode
  accent: string
  ctaLabel: string
  periodLabel: string
  includesQuestionLibrary?: boolean
}

export type PricingTab = PlanCategory | "interview-questions"

export const TABS: {
  id: PricingTab
  label: string
  icon: React.ReactNode
}[] = [
  { id: "subscription", label: "Subscription", icon: <RefreshCw className="w-4 h-4" /> },
  { id: "credit-pack", label: "Credits Only", icon: <Coins className="w-4 h-4" /> },
  { id: "interview-questions", label: "Interview Questions", icon: <BookOpen className="w-4 h-4" /> },
]

// Mock-interview credit packs render on /mock-interview/pricing, so the
// "mock-credit-pack" category isn't represented here. Using Partial<> over Record<PlanCategory,...>
// so we don't have to add a no-op entry just to satisfy the type system.
export const TAB_STATS: Partial<Record<PlanCategory, { icon: React.ReactNode; label: string }[]>> =
  {
    "credit-pack": [
      { icon: <RotateCcw className="w-4 h-4" />, label: "Refund if Tool Ever Visible" },
      { icon: <TimerOff className="w-4 h-4" />, label: "Credits Never Expire" },
      { icon: <Coins className="w-4 h-4" />, label: "1 Call Credit = 1 Hour" },
    ],
    subscription: [
      { icon: <RotateCcw className="w-4 h-4" />, label: "Refund if Tool Ever Visible" },
      { icon: <InfinityIcon className="w-4 h-4" />, label: "Unlimited Credits" },
      { icon: <XCircle className="w-4 h-4" />, label: "Cancel Any Time" },
    ],
  }

type PlanCardConfigWithSplit = PlanCardConfig & {
  // For credit packs that advertise a "X + Y Free" split. The total still
  // matches PLANS[planId].interviewCallCredits.
  callCreditsSplit?: { paid: number; free: number }
  callCreditsHeadline: string
}

// Same reasoning as TAB_STATS — mock_* packs live on their own page,
// so we only define cards for the subscription / starter / basic / power
// plans that this page actually renders.
export const PLAN_CARDS: Partial<Record<PlanName, PlanCardConfigWithSplit>> = {
  // Credit packs
  starter: {
    displayName: "Starter",
    description: "Try PhantomCodeAI for a few interviews — credits never expire.",
    callCreditsHeadline: "3 Call Credits",
    features: [
      "20 online round credits + 10 free",
      "3 interview call credits",
      "Credits never expire",
      "Access all AI models",
      "20+ stealth features",
    ],
    icon: <Coins className="w-6 h-6" />,
    accent: "blue",
    ctaLabel: "Get Credits",
    periodLabel: "one-time",
  },
  basic: {
    displayName: "Basic",
    description: "Most picked credit pack for serious prep.",
    callCreditsSplit: { paid: 6, free: 2 },
    callCreditsHeadline: "6 Call Credits + 2 Free",
    features: [
      "60 online round credits + 20 free",
      "6 interview call credits + 2 free",
      "Credits never expire",
      "Priority support",
      "All stealth features",
    ],
    icon: <Star className="w-6 h-6" />,
    accent: "yellow",
    ctaLabel: "Get Credits",
    periodLabel: "one-time",
  },
  power: {
    displayName: "Power",
    description: "Bulk credits for full interview cycles at top tier companies.",
    callCreditsSplit: { paid: 9, free: 6 },
    callCreditsHeadline: "9 Call Credits + 6 Free",
    features: [
      "90 online round credits + 60 free",
      "9 interview call credits + 6 free",
      "Credits never expire",
      "Priority 24/7 support",
      "All premium AI models",
    ],
    icon: <Zap className="w-6 h-6" />,
    accent: "purple",
    ctaLabel: "Get Credits",
    periodLabel: "one-time",
  },
  // Subscription
  weekly: {
    displayName: "Weekly",
    description: "Unlimited usage for focused interview weeks, renewed weekly.",
    callCreditsHeadline: "Unlimited Calls",
    features: [
      "Unlimited interview call credits",
      "Unlimited online round credits",
      "All AI models",
      "20+ stealth features",
      "Free: 1 AI Mock interview included",
      "Renews weekly — cancel any time, keep your paid period",
    ],
    icon: <Calendar className="w-6 h-6" />,
    accent: "purple",
    ctaLabel: "Subscribe",
    periodLabel: "week",
  },
  monthly: {
    includesQuestionLibrary: true,
    displayName: "Monthly",
    description: "Unlimited usage, renewed monthly. Cancel any time.",
    callCreditsHeadline: "Unlimited Calls",
    features: [
      "Unlimited interview call credits",
      "Unlimited online round credits",
      "All AI models",
      "20+ stealth features",
      "Free: 1 AI Mock interview included",
      "Renews monthly — cancel any time, keep your paid period",
    ],
    icon: <Calendar className="w-6 h-6" />,
    accent: "blue",
    ctaLabel: "Subscribe",
    periodLabel: "month",
  },
  yearly: {
    includesQuestionLibrary: true,
    displayName: "Yearly",
    description: "Best value — a full year of unlimited usage, renewed yearly.",
    callCreditsHeadline: "Unlimited Calls · Best Value",
    features: [
      "Unlimited interview call credits",
      "Unlimited online round credits",
      "All AI models",
      "Priority support",
      "Free: 1 AI Mock interview included",
      "Renews yearly — cancel any time, keep your paid period",
    ],
    icon: <Calendar className="w-6 h-6" />,
    accent: "yellow",
    ctaLabel: "Subscribe",
    periodLabel: "year",
  },
}

export const ACCENT_STYLES: Record<
  string,
  { iconBg: string; iconColor: string; gradient: string }
> = {
  blue: {
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-500",
    gradient: "from-blue-500/20 to-transparent",
  },
  yellow: {
    iconBg: "bg-yellow-500/10",
    iconColor: "text-yellow-500",
    gradient: "from-yellow-500/20 to-transparent",
  },
  purple: {
    iconBg: "bg-purple-500/10",
    iconColor: "text-purple-500",
    gradient: "from-purple-500/20 to-transparent",
  },
}
